import { Injectable } from '@angular/core';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    WorkflowMonitorService,
    Modal,
    ModalService,
    CheckableRowsService,
    CheckableRowsServiceFactory
} from '@lsq-frontend/common-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { ListRelationshipsForBulkEligibilityDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-eligibility-dto';
import { ListRelationshipsForBulkEnrollmentInviteDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-enrollment-invite-dto';
import { ListRelationshipsForBulkFeeStructureDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-fee-structure-dto';
import { BulkActionConfirmationModalComponent } from './bulk-action-confirmation-modal.component';

export enum BulkActionType {
    EnableEligibility = 'EnableEligibility',
    DisableEligibility = 'DisableEligibility',
    EnableSCF = 'EnableSCF',
    DisableSCF = 'DisableSCF',
    SendEnrollmentInvite = 'SendEnrollmentInvite',
    SetFeeStructure = 'SetFeeStructure'
}

@Injectable({ providedIn: 'root' })
export class AdminSuppliersBulkActionsService {
    private _bulkActionTypeSubject = new BehaviorSubject<BulkActionType>(null);
    public bulkActionType$ = this._bulkActionTypeSubject.asObservable();

    private _relationshipKeysSubject = new BehaviorSubject<Guid[]>(null);
    public relationshipKeys$ = this._relationshipKeysSubject.asObservable();

    private _bulkActionCompleteSubject = new Subject();
    public bulkActionComplete$ = this._bulkActionCompleteSubject.asObservable();

    private _isSubmittingBulkActionSubject = new BehaviorSubject<boolean>(false);
    public isSubmittingBulkAction$ = this._isSubmittingBulkActionSubject.asObservable();

    public checkableRowsService: CheckableRowsService;

    constructor(
        private _checkableRowsServiceFactory: CheckableRowsServiceFactory,
        private _modalService: ModalService,
        private _workflowMonitorService: WorkflowMonitorService
    ) {}

    public initializeBulkEligibilityAction(relationshipKeys: Guid[], enable: boolean): void {
        this.checkableRowsService = this._checkableRowsServiceFactory.create();
        this.checkableRowsService.addRows(relationshipKeys);
        this.checkableRowsService.checkAll();

        this._relationshipKeysSubject.next(relationshipKeys);

        this._bulkActionTypeSubject.next(
            enable ? BulkActionType.EnableEligibility : BulkActionType.DisableEligibility
        );
        this._workflowMonitorService.markWorkflowStarted();
    }

    public initializeBulkScfEnabledAction(relationshipKeys: Guid[], enable: boolean): void {
        this.checkableRowsService = this._checkableRowsServiceFactory.create();
        this.checkableRowsService.addRows(relationshipKeys);
        this.checkableRowsService.checkAll();

        this._relationshipKeysSubject.next(relationshipKeys);

        this._bulkActionTypeSubject.next(
            enable ? BulkActionType.EnableSCF : BulkActionType.DisableSCF
        );
        this._workflowMonitorService.markWorkflowStarted();
    }

    public initializeBulkEnrollmentInviteAction(relationshipKeys: Guid[]): void {
        this.checkableRowsService = this._checkableRowsServiceFactory.create();
        this.checkableRowsService.addRows(relationshipKeys);
        this.checkableRowsService.checkAll();

        this._relationshipKeysSubject.next(relationshipKeys);

        this._bulkActionTypeSubject.next(BulkActionType.SendEnrollmentInvite);
        this._workflowMonitorService.markWorkflowStarted();
    }

    public initializeBulkFeeStructureAction(relationshipKeys: Guid[]): void {
        this.checkableRowsService = this._checkableRowsServiceFactory.create();
        this.checkableRowsService.addRows(relationshipKeys);
        this.checkableRowsService.checkAll();

        this._relationshipKeysSubject.next(relationshipKeys);

        this._bulkActionTypeSubject.next(BulkActionType.SetFeeStructure);
        this._workflowMonitorService.markWorkflowStarted();
    }

    public cancelBulkAction(): void {
        this._bulkActionTypeSubject.next(null);
        this._relationshipKeysSubject.next(null);
    }

    public setIsSubmitting(isSubmitting: boolean): void {
        this._isSubmittingBulkActionSubject.next(isSubmitting);
    }

    public showConfirmationModal(
        modalTitle: string,
        confirmationText: string,
        relationships:
            | ListRelationshipsForBulkEligibilityDto[]
            | ListRelationshipsForBulkFeeStructureDto[]
            | ListRelationshipsForBulkEnrollmentInviteDto[],
        options?: {
            feeType?: string;
            feeRate?: string;
            enrollmentNotificationEmails?: string[];
        }
    ): void {
        const closeAction = () => {
            this._modalService.closeModal();
            this.cancelBulkAction();
            this._bulkActionCompleteSubject.next();
            this._workflowMonitorService.markWorkflowFinished();
        };

        this._modalService.showModal(
            new Modal({
                title: modalTitle,
                bodyComponent: BulkActionConfirmationModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: false,
                showCloseButton: true,
                okButtonSize: 'block',
                okButtonText: 'Return To Suppliers',
                width: '55rem',
                data: {
                    confirmationText,
                    relationships,
                    feeType: options?.feeType,
                    feeRate: options?.feeRate,
                    enrollmentNotificationEmails: options?.enrollmentNotificationEmails
                },
                onOkClick: closeAction,
                onBackdropClick: closeAction,
                onCloseButtonClick: closeAction
            })
        );
    }
}
